import type * as React from "react";
import { type ReactNode } from "react";

import { useSelector } from "react-redux";
import classNames from "classnames";
import * as PriceUtils from "../../../utils/PriceUtils";
import { zero, zeroNetPrice } from "../../../utils/PriceUtils";
import { DateFormatType, formatDate } from "../../../utils/DateFormatter";
import Spacer from "../../../common/components/Spacer";
import SimplePriceText, { PriceTextVariants } from "../../../common/components/SimplePriceText/SimplePriceText";
import OptimeSolutionsMessage from "../../../common/components/Messages/OptimeSolutionsMessage";
import { AliasesParameter } from "../../../common/components/Product/ProductParams";
import { ViewablePackagingCell } from "../../../Cart/components/CartPage/ProductTable/EditableEntry/PackagingCell";
import { getTotalPrice, isProductPMA } from "../../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { getVisibleNetPrice } from "../../../Cart/redux/selectors/productEntrySelectors";
import { isSpecialPrice } from "~/ProductDetails/redux/productDetailsSelectors";
import { obtainSpecialPriceLabel } from "~/common/components/Price/v1/Price";
import { useTranslation } from "react-i18next";
import { InfoProductMessagePMA } from "~/ProductDetails/components/ProductSummary/InfoProductMessagePMA";
import { selectLineItem } from "~/Cart/redux/selectors/cartSelectors";
import { ProductService } from "~/AddToCart/ProductService";
import { PriceReferenceParam } from "~/Cart/components/LineItemEditableParams";
import { Stacker } from "~/common/components/Stacker/Stacker";
import { FCCTag } from "~/FreeCapacityPush/components/FCCTag/FCCTag";
import classes from "~/QuoteCart/components/ActiveQuoteEdit/ProductTable/EditablePackageEntry.module.less";
import { Alert } from "~/common/m3/Alert/Alert";

interface Props {
  packEntryId: number;
  index: number;
}

const ViewablePackageEntry = (props: Props) => {
  const { packEntryId } = props;
  const { t } = useTranslation();

  const packageEntry = useSelector(selectLineItem(packEntryId));
  const product = ProductService.useProductDescriptor({
    productCode: packageEntry.productCode,
    priceReference: packageEntry.priceReference,
    options: { fetch: true },
  });

  const isCommentVisible = packageEntry.showComments;

  const quantityVisible = product.base.quantityVisible;
  const isOptime = product.base.isOptime;

  const visiblePrice = getVisibleNetPrice(packageEntry, product) ?? zeroNetPrice(product);
  const priceUnit = visiblePrice.unit;

  const isSpecial = isSpecialPrice(product, packageEntry.packCode ?? "");

  const rdd = packageEntry.deliveryDate ? formatDate(packageEntry.deliveryDate, DateFormatType.DATE) : "–";

  const total = getTotalPrice(visiblePrice, packageEntry.quantity);

  const isFcc = product.base.fifteenDigit[packageEntry.packCode]?.isFcc;

  return (
    <div data-test-id={`package-entry`}>
      <CellRow>
        <Cell data-test-id={"package-entry-quantity"} first={true} className={classes.qtyCell}>
          {packageEntry.quantity}
          {isFcc && <FCCTag anchor={"left"} className={classes.fccTag} />}
        </Cell>

        <Cell>
          <div>
            <ViewablePackagingCell lineItem={packageEntry} product={product} />

            <AliasesParameter
              className={"mt-3"}
              product={product.base}
              mode={"15-digits"}
              packCode={packageEntry.packCode}
            />
          </div>
        </Cell>

        <Cell>
          {quantityVisible ? (
            <div>
              <p className={"my-0"}>{rdd}</p>
            </div>
          ) : (
            <div>{"-"}</div>
          )}
        </Cell>

        <Cell className={"d-flex flex-direction-column"}>
          <Stacker gap={8}>
            <div>
              <div data-test-id={"package-entry-offer-price"}>{PriceUtils.text(visiblePrice || zero())}</div>
              <div className={"mt-1 f-label-color"}>
                {
                  <SimplePriceText
                    priceUnit={priceUnit}
                    variant={quantityVisible ? null : PriceTextVariants.monthlySubsription}
                  />
                }
              </div>
              {isSpecial && <div className={"scha__customer-special-price"}>{obtainSpecialPriceLabel(t)}</div>}
            </div>
            <PriceReferenceParam
              product={product.base}
              priceReference={packageEntry.priceReference}
              data-test-id={"package-entry-price-reference"}
              disabled={true}
              editable={false}
            />
          </Stacker>
        </Cell>

        <Cell className={"d-flex flex-direction-column"} last={true}>
          <strong data-test-id={"package-entry-total-price"}>{PriceUtils.text(total)}</strong>
          {!quantityVisible && (
            <div className={"mt-1 f-label-color"}>
              <SimplePriceText
                priceUnit={priceUnit}
                variant={quantityVisible ? null : PriceTextVariants.yearlySubscription}
              />
            </div>
          )}
        </Cell>
      </CellRow>

      {isCommentVisible && (
        <Row className={"mt-3"}>
          <p className={"my-0 f-label-color"}>{t("activeQuotePage.comment.title")}</p>
          <p className={"mt-1 mb-0"} data-test-id={"cart-package-entry-comment"}>
            {packageEntry.comment}
          </p>
        </Row>
      )}
      {/* spacer without border */}
      {(!quantityVisible || isOptime) && <div className={"mt-2 mb-0"}></div>}
      {!quantityVisible && (
        <Alert severity="info" className="mt-2 w-100">
          {t("subscriptionProduct.DST.billingInfo")}
        </Alert>
      )}
      {isProductPMA(product) && <InfoProductMessagePMA className={"mt-2"} />}
      {isOptime && <OptimeSolutionsMessage className="mt-2 w-100" />}
      <Spacer className={"mt-3 mb-0"} />
    </div>
  );
};

const CellRow = ({ children }: { children?: ReactNode }) => (
  <div className={"scha__active-quote__product-table__right-side__col-container"}>{children}</div>
);

const Cell: React.FunctionComponent<{
  alignEnd?: boolean;
  first?: boolean;
  last?: boolean;
  className?: string;
  "data-test-id"?: string;
  children?: ReactNode;
}> = ({ children, alignEnd, first, last, className, ...rest }) => {
  const cls = classNames(
    "scha__cart__product-table__body-cell",
    {
      "first-on-right": first,
      "last-on-right": last,
      "flex-justify-end d-flex": alignEnd,
    },
    className,
  );
  return (
    <div className={cls} {...rest}>
      {children}
    </div>
  );
};

const Row: React.FunctionComponent<{ className?: string; children?: ReactNode }> = ({ children, className }) => (
  <div className={classNames(className, "px-3")}>{children}</div>
);

export default ViewablePackageEntry;
